import { getBaseRole } from '@/helpers/customRoles';
import { FC, useMemo, useState } from 'react'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import Modal from '@common/Modal'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import classNames from 'classnames'
import { ITimetableReportItem } from '@pages/Journal2/components/Tabs/KTPJournal'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import TextAreaField from '@pages/Admin/CourseSettings/components/TextAreaField/TextAreaField'
import cn from './index.module.css'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import { useAddAlert } from '@/hooks/useAddAlert'
import { changeLessonDuration, changeLessonSalary } from './utils/api'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { Modal as AntModal } from 'antd'
import CheckboxViolet from '@UI/New/ChecboxViolet/CheckboxViolet'
import {
  makeLessonCancel,
  makeLessonCompleted,
} from '@pages/Main/LessonReports/utils/api'
import {
  isLessonStatusCommentRequired,
  shouldSaveLessonSalary,
} from '@pages/Main/LessonReports/helpers/getLessonStatusOptions'

type EditableLessonReport = ITimetableReportItem & {
  salary?: number | null
  sallary?: number | null
  salary_for_done?: number | null
  is_course_deleted?: number
}

interface IChangeLessonReportStatusModal {
  open: boolean
  report: EditableLessonReport | null
  onSuccess: () => void
  onDismiss: () => void
}

const ChangeLessonReportStatusModal: FC<IChangeLessonReportStatusModal> = (
  props,
) => {
  return (
    <Modal
      active={props.open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={() => props.onDismiss()}
    >
      <Body {...props} />
    </Modal>
  )
}

const statusOptions = [
  { label: 'Проведено', value: '5' },
  { label: 'Отмена учеником', value: '10' },
  { label: 'Отмена педагогом', value: '11' },
  { label: 'Платная отмена', value: '12' },
]

const Body: FC<IChangeLessonReportStatusModal> = (props) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState(false)
  const [salaryChanged, setSalaryChanged] = useState(false)
  const role = useSelector((store: any) => store.profile?.profile?.role)
  const canEditSalary = !['seminarian', 'senior-seminarian'].includes(getBaseRole(role))
  const extensionDays = useSelector((store: any) => Math.max(
    1,
    Number(store.settings?.groupAccessExtensionDaysOnLessonCancellation) || 1,
  ))
  const reportSalary = props.report?.salary ?? props.report?.sallary ?? 0
  const salaryForDone = props.report?.salary_for_done ?? reportSalary
  const reportStatus = String(props.report?.status)
  const initialStatus = statusOptions.some((item) => item.value === reportStatus)
    ? reportStatus
    : '5'
  const initialSalary = reportStatus === '5' ? reportSalary : salaryForDone
  const [form, setForm] = useState({
    status: initialStatus,
    comment: String(props.report?.comment ?? ''),
    duration: props.report?.duration ?? 0,
    salary: Number(initialSalary ?? 0),
    extendGroupAccess: true,
  })

  const options = useMemo(() => {
    return statusOptions.filter((item) => {
      if (props.report?.conference_type === 'introduction') {
        return item.value !== '12'
      }
      // Курс у ученика удалён — списать платную отмену не с чего
      if (props.report?.is_course_deleted && String(props.report?.status) !== '12') {
        return item.value !== '12'
      }
      return true
    })
  }, [props.report])

  const onSave = async () => {
    setLoading(true)
    await saveHandle()
    setLoading(false)
  }

  const saveHandle = async () => {
    const isCommentRequired = isLessonStatusCommentRequired(
      form.status,
      props.report?.conference_type,
    )

    if (isCommentRequired && !form.comment.trim()) {
      addAlert({ title: 'Нужно заполнить комментарий' })
      return
    }

    const appliedExtension = props.report?.cancelled_group_access_extension
    let revokeGroupAccessExtension = false
    if (
      form.status === '5' &&
      ['10', '11'].includes(String(props.report?.status)) &&
      Number(appliedExtension?.days) > 0
    ) {
      revokeGroupAccessExtension = await new Promise<boolean>((resolve) => {
        AntModal.confirm({
          title: 'Отменить продление обучения?',
          content: `Вы продлевали баланс всем на ${appliedExtension?.days} дней, так как занятие было отменено. Отменить всем это продление?`,
          okText: 'Отменить продление',
          cancelText: 'Оставить продление',
          centered: true,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
    }

    let cancelErrorMessage = ''
    const resChangeStatus = form.status === '5'
      ? await makeLessonCompleted(
          props.report!.id,
          form.comment,
          props.report!.date_conference,
          form.duration,
          undefined,
          revokeGroupAccessExtension,
        )
      : await makeLessonCancel(
          props.report!.id,
          form.comment,
          form.status,
          undefined,
          form.extendGroupAccess,
          (message) => {
            cancelErrorMessage = message
          },
        )

    if (!resChangeStatus) {
      addAlert({
        title: 'Ошибка',
        text: cancelErrorMessage || 'Попробуйте ещё раз или обратитесь к администратору',
        type: 'error',
      })
      return
    }

    const resChangeDuration = form.status === '5'
      ? true
      : await changeLessonDuration(props.report!.id, form.duration)

    if (!resChangeDuration) {
      addAlert({
        title: 'Ошибка',
        text: 'Статус сохранён, но длительность обновить не удалось',
        type: 'error',
      })
      props.onSuccess()
      return
    }

    if (shouldSaveLessonSalary(
      canEditSalary,
      props.report?.conference_type,
      salaryChanged,
    )) {
      const resChangeSalary = await changeLessonSalary(props.report!.id, form.salary)
      if (!resChangeSalary) {
        addAlert({
          title: 'Ошибка',
          text: 'Статус сохранён, но ЗП за занятие обновить не удалось',
          type: 'error',
        })
        props.onSuccess()
        return
      }
    }

    addAlert({ title: 'Данные занятия обновлены' })
    props.onSuccess()
  }

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>Редактировать отчет о занятии</span>
        </div>
        <div className={mclasses.closeIcon} onClick={props.onDismiss}>
          <IcoRemove />
        </div>
      </div>
      <div className={mclasses.formWrapper}>
        {!!props.report?.is_course_deleted && (
          <div className={cn.extensionInfo}>
            Курс у ученика удалён. ЗП и цена считаются по ставкам удалённого заказа, платная отмена недоступна.
          </div>
        )}
        <SelectField
          className={cn.select}
          label="Статус"
          value={form.status}
          options={options}
          onChange={(value) => {
            if (typeof value === 'string') {
              setForm((prev) => ({
                ...prev,
                status: value,
                salary: !salaryChanged && value === '5' ? salaryForDone : prev.salary,
              }))
            }
          }}
        />
        <TextAreaField
          className={cn.textarea}
          label={'Комментарий'}
          placeholder="комментарий"
          value={form.comment}
          onChange={(value) => {
            setForm((prev) => ({ ...prev, comment: value.target.value }))
          }}
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
        {Number(props.report?.cancelled_group_access_extension?.days) > 0 && (
          <div className={cn.extensionInfo}>
            По этому занятию обучение продлено на{' '}
            {props.report?.cancelled_group_access_extension?.days} дней
            {Number(props.report?.cancelled_group_access_extension?.students_count) > 0
              ? ` для ${props.report?.cancelled_group_access_extension?.students_count} учащихся`
              : ''}.
          </div>
        )}
        {!!props.report?.can_extend_cancelled_group_access && ['10', '11'].includes(form.status) && (
          <CheckboxViolet
            text={`Продлить всем учащимся группы обучение по данному курсу на ${extensionDays} дней`}
            value={form.extendGroupAccess}
            className={undefined}
            textClassName={undefined}
            blockClassName={undefined}
            onClick={undefined}
            setValue={() => setForm((prev) => ({
              ...prev,
              extendGroupAccess: !prev.extendGroupAccess,
            }))}
          />
        )}
        <TextField
          label="Длительность"
          className={cn.textarea}
          value={String(form.duration)}
          onChange={(value) => {
            if (Number.isNaN(Number(value))) return
            setForm((prev) => ({ ...prev, duration: Number(value) }))
          }}
        />
        <TextField
          label="ЗП педагога"
          className={cn.textarea}
          value={String(form.salary)}
          disabled={!canEditSalary}
          extraContent={
            <TooltipWrapper title="Здесь вы можете изменить зарплату педагога за конкретное занятие. Важно: вы не меняете структуру формирования зарплаты по данному предмету и данной группе у педагога, а лишь меняете начисленную сумму за одно конкретное занятие.">
              <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
                <InfoCircleOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </span>
            </TooltipWrapper>
          }
          description={
            canEditSalary
              ? 'Можно изменить сумму начисления только за это занятие.'
              : 'Педагог видит расчетную сумму, но не может изменить себе ЗП.'
          }
          onChange={(value) => {
            if (Number.isNaN(Number(value))) return
            setSalaryChanged(true)
            setForm((prev) => ({ ...prev, salary: Number(value) }))
          }}
        />
      </div>
      <div className={mclasses.actions}>
        <Button color="gray" onClick={props.onDismiss}>
          Закрыть
        </Button>
        <Button color="primary" onClick={onSave} disabled={loading} isLoading={loading}>
          Сохранить
        </Button>
      </div>
    </div>
  )
}

export default ChangeLessonReportStatusModal
